import { IMPACT_DAYS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Icon, Input, Skeleton } from "@sagot/ui";
import { useRef, useState } from "react";
import { deliversEvents, impactLines, isHighImpactKey, type ImpactLine } from "./defaults-model";
import { useWorkflowImpact } from "./use-defaults";

const P = "engage.workflows.defaults.pause";
const H = "engage.workflows.defaults.service.pause";

/** What a default needs to say before it is paused. */
export interface PauseTarget {
  id: string;
  name: string;
  area?: string | null;
  /** The catalogue's sentence about what stops (in the workspace's language). */
  pause_impact?: string | null;
  /** The default's catalogue key: a high-impact default is known by it even while its numbers are still loading. */
  system_key?: string | null;
}

interface PauseDefaultDialogProps {
  workflow: PauseTarget;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Focus goes here when the dialog closes (the control that opened it, which a menu item no longer is). */
  onCloseAutoFocus?: (event: Event) => void;
}

function lineText(line: ImpactLine, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (line.kind === "alerts") return t(`${P}.alerts`, { count: line.count, people: t(`${P}.people`, { count: line.people ?? 0 }) });
  return t(`${P}.${line.kind}`, { count: line.count });
}

interface TypeToConfirmProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
}

/** A high-impact default (WD-04 sends the client every staff reply) is paused only by someone who types its name (WF-009). */
function TypeToConfirm({ name, value, onChange }: TypeToConfirmProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3" data-testid="pause-confirm">
      <p className="flex items-start gap-2 rounded-md border border-danger bg-danger-soft p-3 text-sm font-semibold" role="alert">
        <Icon name="alert-octagon" className="mt-0.5 size-4 shrink-0 text-danger" />
        {t(`${H}.highImpact`)}
      </p>
      <Field label={t(`${H}.typeLabel`)} hint={t(`${H}.typeHint`, { name })}>
        <Input autoComplete="off" spellCheck={false} value={value} onChange={(e) => { onChange(e.target.value); }} />
      </Field>
    </div>
  );
}

/**
 * Pausing a default really stops it for everyone, so it asks first and says what it would stop: the catalogue's sentence, then
 * what the workflow did in the last 30 days. Cancel has focus. The numbers are an aid: when they cannot be read the person can still pause.
 * A high-impact default asks for more: its name must be typed before Pause works (the numbers say which; the key does while they load).
 */
export function PauseDefaultDialog({ workflow, busy, onConfirm, onClose, onCloseAutoFocus }: PauseDefaultDialogProps) {
  const { t } = useTranslation();
  const impact = useWorkflowImpact(workflow.id, true);
  const cancel = useRef<HTMLButtonElement>(null);
  const [typed, setTyped] = useState("");
  const deliveries = deliversEvents(workflow);
  const lines = impact.data ? impactLines(impact.data) : [];
  const highImpact = impact.data ? impact.data.high_impact : isHighImpactKey(workflow.system_key);
  const confirmName = impact.data?.confirm_name ?? workflow.name;
  const confirmed = !highImpact || typed.trim() === confirmName;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${P}.title`, { name: workflow.name })}
        description={workflow.pause_impact ?? undefined}
        closeLabel={t("common.close")}
        onOpenAutoFocus={(event) => { event.preventDefault(); cancel.current?.focus(); }}
        onCloseAutoFocus={onCloseAutoFocus}
        footer={
          <>
            <Button ref={cancel} variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={busy} disabled={!confirmed} onClick={onConfirm}>{t(deliveries ? `${P}.confirmDeliveries` : `${P}.confirmAlerts`)}</Button>
          </>
        }
      >
        <div className="space-y-3" data-testid="pause-impact">
          {impact.isPending ? (
            <div aria-busy="true">
              <span className="sr-only" role="status">{t(`${P}.loading`)}</span>
              <Skeleton className="h-4 w-48" aria-hidden="true" />
              <Skeleton className="mt-2 h-4 w-64 max-w-full" aria-hidden="true" />
            </div>
          ) : null}
          {impact.isError ? <p className="flex items-start gap-2 text-sm text-fg-muted" role="status"><Icon name="info" className="mt-0.5 size-4 shrink-0" />{t(`${P}.impactFailed`)}</p> : null}
          {impact.data && lines.length > 0 ? (
            <div>
              <p className="text-sm font-semibold">{t(`${P}.stops`)}</p>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
                {lines.map((line) => <li key={line.kind} data-testid={`impact-${line.kind}`}>{lineText(line, t)}</li>)}
              </ul>
              <p className="mt-1 text-xs text-fg-subtle">{t(`${P}.window`, { days: impact.data.days })}</p>
            </div>
          ) : null}
          {impact.data && lines.length === 0 ? <p className="text-sm text-fg-muted">{t(`${P}.nothing`, { days: impact.data.days || IMPACT_DAYS })}</p> : null}
          <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(deliveries ? `${P}.endpointsKeep` : `${P}.memberSettings`)}</p>
          {highImpact ? <TypeToConfirm name={confirmName} value={typed} onChange={setTyped} /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
